import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  TablePagination,
  Skeleton,
  TextField,
  MenuItem,
  InputAdornment
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ModeIcon from "@mui/icons-material/Mode";
import SearchIcon from '@mui/icons-material/Search';

import { getSuppliers, patchSupplier } from "../../collections/suppliers";

const SuppliersList = (props) => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [forceRerender, setForceRerender] = useState(false);

  useEffect(() => {
    props.setPageTitle("Nhà cung cấp");
    const fetchSuppliers = async () => {
      setLoading(true);
      try {
        const params = {
          page: page + 1,
          per_page: rowsPerPage,
          status: "All",
        };
        const res = await getSuppliers(params);
        setSuppliers(res.data);
        setTotalRows(res.total);
      } catch (error) {
        console.error("Failed to fetch suppliers", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSuppliers();
  }, [page, rowsPerPage, props, forceRerender]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditSupplier = (id) => {
    navigate(`/suppliers/${id}/edit`);
  };

  const handleStatusChange = async (supplierId, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    const confirmAction = window.confirm(
      `Bạn có chắc muốn ${newStatus === 'Active' ? 'kích hoạt' : 'vô hiệu hóa'} nhà cung cấp này không?`
    );

    if (confirmAction) {
      try {
        await patchSupplier(supplierId, { status: newStatus });
        setForceRerender(prev => !prev); // Trigger a re-fetch
      } catch (error) {
        console.error("Failed to update status", error);
      }
    }
  };

  const translateStatus = (status) => {
    switch (status) {
      case "Active":
        return "Hoạt động";
      case "Inactive":
        return "Không hoạt động";
      default:
        return status;
    }
  };

  if (loading) {
    return <Skeleton animation="wave" variant="rectangular" height={250} sx={{ mt: 3 }} />;
  }

  return (
    <Container sx={{ mt: 3 }}>
      <Grid container alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Grid item>
          <Button
            variant="outlined"
            onClick={() => navigate('/suppliers/new')}
            startIcon={<AddIcon />}
          >
            Thêm nhà cung cấp
          </Button>
        </Grid>
      </Grid>
      <TableContainer component={Paper}>
        <Table aria-label="suppliers table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Tên nhà cung cấp</TableCell>
              <TableCell>Điện thoại</TableCell>
              <TableCell>Trạng thái</TableCell>
              <TableCell>Hành động</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {suppliers.map((supplier) => (
              <TableRow
                key={supplier.id}
                hover
                onClick={() => navigate(`/suppliers/${supplier.id}`)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>{supplier.id}</TableCell>
                <TableCell>{supplier.name}</TableCell>
                <TableCell>{supplier.phone}</TableCell>
                <TableCell>{translateStatus(supplier.status)}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleEditSupplier(supplier.id);
                    }}
                    startIcon={<ModeIcon />}
                    sx={{ mr: 1 }}
                  >
                    Chỉnh sửa
                  </Button>
                  <Button
                    variant="outlined"
                    color={supplier.status === 'Active' ? 'error' : 'success'}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleStatusChange(supplier.id, supplier.status);
                    }}
                  >
                    {supplier.status === 'Active' ? 'Vô hiệu hóa' : 'Kích hoạt'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Số hàng mỗi trang"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} của ${count}`}
        />
      </TableContainer>
    </Container>
  );
};

export default SuppliersList; 